import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useEffect, useMemo } from 'react';
import { isNullish } from 'remeda';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { Records } from 'src/graphql/types';
import { useGroupRulesStore } from 'src/store/group-rules';
import { RuleValues, UserGroupRules } from './group-rules.types';

const GET_USER_GROUP_RULE_VALUES_QUERY = gql`
  query GetUserGroupRule @entity(name: "UserGroupRule") {
    getUserGroupRule {
      records {
        id
        name
      }
    }
  }
`;

interface GetUserGroupRuleQuery {
  getUserGroupRule: Records<{
    id: string;
    name: string;
  }>;
}

export interface GetUserGroupRuleValuesQuery {
  getPlatformUser: Records<{
    userGroups: {
      ruleValues: {
        id: string;
        value: string;
        userGroupRuleId: UserGroupRules;
      }[];
    }[];
  }>;
}

export const useUserGroupRuleValues = () => ({
  userGroupRules: useGroupRulesStore((s) => s.userGroupRules),
  loading: useGroupRulesStore((s) => s.userRulesLoading),
});

const useUserGroupRules = (options?: QueryHookOptions<GetUserGroupRuleQuery>) => {
  const { data, ...rest } = useQuery<GetUserGroupRuleQuery>(GET_USER_GROUP_RULE_VALUES_QUERY, {
    ...options,
  });

  const userGroupRules = useMemo(() => data?.getUserGroupRule.records, [data]);

  return { userGroupRules, ...rest };
};

export const useFetchUserGroupRuleValues = () => {
  const { userGroupRules: userGroupRuleValuesData } = useUserGroupRules();
  const { user } = useCurrentUserProfile();

  const setUserGroupRules = useGroupRulesStore((state) => state.setUserGroupRules);
  const userGroupRules = useGroupRulesStore((state) => state.userGroupRules);

  const allUserGroupRuleValues = useMemo(
    () =>
      (userGroupRuleValuesData || []).reduce(
        (acc, groupRule) => {
          return { ...acc, [groupRule.id]: false };
        },
        {} as Record<UserGroupRules, boolean>,
      ),
    [userGroupRuleValuesData],
  );

  useEffect(() => {
    if (!user) return;

    const ruleIds: Record<UserGroupRules, boolean | number[]> = {
      ...allUserGroupRuleValues,
    };
    const parsedRuleIds = {} as Record<UserGroupRules, boolean | number[]>;

    if (!user.userGroups?.length) {
      setUserGroupRules(ruleIds);
      return;
    }

    user.userGroups.forEach((group) => {
      group.ruleValues.forEach((rule) => {
        const ruleId = rule.userGroupRuleId as UserGroupRules;

        if (!(ruleId in parsedRuleIds)) {
          ruleIds[ruleId] =
            ruleId === UserGroupRules.AvailablePartnerIds
              ? isMultiValueRuleEnabled(rule.value)
              : isRuleEnabled(rule.value);
        }

        parsedRuleIds[ruleId] = true;
      });
    });

    setUserGroupRules(ruleIds);
  }, [allUserGroupRuleValues, user, setUserGroupRules]);

  return {
    userGroupRules,
  };
};

function isRuleEnabled(ruleValue: string | null): boolean {
  return ruleValue?.toLocaleLowerCase() === RuleValues.True;
}

/**
 * available_partner_ids rule can contain list of partner ids as a value
 * Example of the value: 123,234
 * This is different from rest of rules and separate handler was created to cover it
 * */
function isMultiValueRuleEnabled(ruleValue: string): boolean | number[] {
  if (ruleValue === RuleValues.All) {
    return true;
  }

  if (isNullish(ruleValue)) {
    return false;
  }

  const partnerIdsInValue = ruleValue
    .split(',')
    .map(Number)
    .filter((partnerId) => !Number.isNaN(partnerId));

  return partnerIdsInValue.length ? partnerIdsInValue : isRuleEnabled(ruleValue);
}
